import React from 'react';
import { z } from 'zod';
import type { ShapeManifest } from '../../../../lib/diagram/shape-manifest.types';
import { getTableCellTextRect } from './tableCellSpacing.utils';
import { escapeSvgAttr, formatSvgNumber, renderCenteredLabel, renderMultilineText } from '../svgRenderer.utils';

const clampCount = (value: number | undefined, fallback: number, min = 1) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const tableCellStyleSchema = z.object({
  fillColor: z.string().optional(),
  textColor: z.string().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.enum(['normal', 'bold']).optional(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  textDecoration: z.enum(['underline', 'line-through', 'underline line-through', 'none']).optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  textRotation: z.union([z.literal(0), z.literal(90), z.literal(-90), z.literal(180)]).optional(),
  fontSize: z.number().optional(),
  padding: z.number().optional(),
  paddingTop: z.number().optional(),
  paddingRight: z.number().optional(),
  paddingBottom: z.number().optional(),
  paddingLeft: z.number().optional(),
  margin: z.number().optional(),
  marginTop: z.number().optional(),
  marginRight: z.number().optional(),
  marginBottom: z.number().optional(),
  marginLeft: z.number().optional(),
  borderColor: z.string().optional(),
  borderWidth: z.number().optional(),
  borderDash: z.array(z.number()).optional(),
});

const mergedRegionSchema = z.object({
  startRow: z.number().int(),
  startCol: z.number().int(),
  endRow: z.number().int(),
  endCol: z.number().int(),
});

const tableShapeProps = z.object({
  rowCount: z.number().int().min(1).default(3),
  columnCount: z.number().int().min(1).default(3),
  rowHeights: z.array(z.number().positive()).optional(),
  columnWidths: z.array(z.number().positive()).optional(),
  headerHeight: z.number().positive().optional(),
  showHeader: z.boolean().default(false),
  showHeaderColumn: z.boolean().optional(),
  headerRowIndices: z.array(z.number().int()).optional(),
  headerColIndices: z.array(z.number().int()).optional(),
  headerFillColor: z.string().default('#F1F5F9'),
  cellFillColor: z.string().default('#FFFFFF'),
  gridColor: z.string().default('#97a3b6'),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  textColor: z.string().default('#0F172A'),
  label: z.string().optional(),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  strokeWidth: z.number().optional(),
  dash: z.array(z.number()).optional(),
  dashEnabled: z.boolean().optional(),
  cellText: z.record(z.string(), z.string()).optional(),
  cellStyles: z.record(z.string(), tableCellStyleSchema).optional(),
  mergedRegions: z.array(mergedRegionSchema).optional(),
  attrs: z.record(z.string(), z.unknown()).optional(),
});

export type TableShapeProps = z.infer<typeof tableShapeProps>;

const DEFAULT_DIMS = { width: 680, height: 240 };
const DEFAULT_HEADER = 60;

const renderKonva = ({ viewModel, props, handlers }: any) => {
  // Dynamic import to avoid pulling react-konva into the worker bundle.
  // This function is only ever called client-side by the Konva canvas renderer.
  const { Rect, Group, Line } = (globalThis as Record<string, unknown>).window
    ? require('react-konva') // eslint-disable-line @typescript-eslint/no-require-imports
    : { Rect: null, Group: null, Line: null };
  const width = viewModel.width ?? DEFAULT_DIMS.width;
  const height = viewModel.height ?? DEFAULT_DIMS.height;
  const rowCount = clampCount(props.rowCount, 3);
  const columnCount = clampCount(props.columnCount, 3);
  const showHeader = props.showHeader !== false;
  const headerHeight = showHeader ? (props.headerHeight ?? DEFAULT_HEADER) : 0;
  const bodyHeight = Math.max(1, height - headerHeight);
  const minCell = 20;
  const normalize = (arr: number[], total: number, fallbackCount: number) => {
    const sum = arr.reduce((s, n) => s + n, 0);
    if (!Number.isFinite(sum) || sum <= 0) {
      const even = total / fallbackCount;
      return Array(fallbackCount).fill(even);
    }
    const scale = total / sum;
    return arr.map((n) => Math.max(minCell, n * scale));
  };
  const rowHeights = normalize(
    props.rowHeights?.length === rowCount ? props.rowHeights : Array(rowCount).fill(bodyHeight / rowCount),
    bodyHeight,
    rowCount,
  );
  const columnWidths = normalize(
    props.columnWidths?.length === columnCount ? props.columnWidths : Array(columnCount).fill(width / columnCount),
    width,
    columnCount,
  );
  const gridColor = props.gridColor || '#97a3b6';
  const cellFillColor = props.cellFillColor || '#FFFFFF';
  const headerFillColor = props.headerFillColor || '#F1F5F9';

  let runningX = 0;
  const verticalLines = Array.from({ length: Math.max(0, columnCount - 1) }, (_, idx) => {
    runningX += columnWidths[idx];
    const x = runningX;
    return React.createElement(Line, {
      key: `v-${idx}`,
      points: [x, 0, x, height],
      stroke: gridColor,
      strokeWidth: 1,
    });
  });

  let runningY = showHeader ? headerHeight : 0;
  const horizontalLines = Array.from({ length: Math.max(0, rowCount + (showHeader ? 1 : 0) - 1) }, (_, idx) => {
    if (showHeader && idx === 0) {
      return React.createElement(Line, {
        key: 'h-header',
        points: [0, headerHeight, width, headerHeight],
        stroke: gridColor,
        strokeWidth: 1,
      });
    }
    if (showHeader && idx === 0) return null as any;
    if (showHeader) {
      runningY = headerHeight + rowHeights[0];
    } else {
      runningY = rowHeights[0];
    }
    if (idx > 0) {
      runningY = headerHeight + rowHeights.slice(0, idx).reduce((s, n) => s + n, 0);
    }
    const y = runningY;
    return React.createElement(Line, {
      key: `h-${idx}`,
      points: [0, y, width, y],
      stroke: gridColor,
      strokeWidth: 1,
    });
  });

  const children: React.ReactNode[] = [];
  children.push(React.createElement(Rect, {
    key: 'bg',
    x: 0,
    y: 0,
    width,
    height,
    fill: cellFillColor,
    stroke: gridColor,
    strokeWidth: 1,
    cornerRadius: 6,
  }));
  if (showHeader) {
    children.push(React.createElement(Rect, {
      key: 'header',
      x: 0,
      y: 0,
      width,
      height: headerHeight,
      fill: headerFillColor,
      stroke: gridColor,
      strokeWidth: 1,
      cornerRadius: [6, 6, 0, 0] as any,
    }));
  }
  children.push(...verticalLines.filter(Boolean) as React.ReactNode[]);
  children.push(...horizontalLines.filter(Boolean) as React.ReactNode[]);

  return React.createElement(
    Group as unknown as React.ComponentType<any>,
    { x: viewModel.x, y: viewModel.y, width, height, ...handlers },
    ...children,
  );
};

const renderSvg = (dimensions: { width: number; height: number }, props: TableShapeProps) => {
  const width = dimensions.width ?? DEFAULT_DIMS.width;
  const height = dimensions.height ?? DEFAULT_DIMS.height;
  const attrs = (props as any)?.attrs as Record<string, unknown> | undefined;
  const rowCount = clampCount(props.rowCount ?? attrs?.rowCount as number | undefined, 3);
  const columnCount = clampCount(props.columnCount ?? attrs?.columnCount as number | undefined, 3);
  const showHeader = (props.showHeader ?? attrs?.showHeader) !== false;
  const showHeaderColumn = (props.showHeaderColumn ?? (attrs as any)?.showHeaderColumn) === true;
  const headerHeight = showHeader ? ((props.headerHeight ?? (attrs?.headerHeight as number | undefined)) ?? DEFAULT_HEADER) : 0;
  const headerRowIndicesRaw = (props.headerRowIndices ?? (attrs?.headerRowIndices as number[] | undefined)) ?? (showHeader ? [0] : []);
  const headerColIndicesRaw = (props.headerColIndices ?? (attrs?.headerColIndices as number[] | undefined)) ?? (showHeaderColumn ? [0] : []);
  const bodyHeight = Math.max(1, height - headerHeight);
  const minCell = 20;
  const normalize = (arr: number[], total: number, fallbackCount: number) => {
    const sum = arr.reduce((s, n) => s + n, 0);
    if (!Number.isFinite(sum) || sum <= 0) {
      const even = total / fallbackCount;
      return Array(fallbackCount).fill(even);
    }
    const scale = total / sum;
    return arr.map((n) => Math.max(minCell, n * scale));
  };
  const rowHeightsRaw = (props.rowHeights ?? (attrs?.rowHeights as number[] | undefined)) ?? [];
  const colWidthsRaw = (props.columnWidths ?? (attrs?.columnWidths as number[] | undefined)) ?? [];
  const rowHeights = normalize(
    rowHeightsRaw.length === rowCount ? rowHeightsRaw : Array(rowCount).fill(bodyHeight / rowCount),
    bodyHeight,
    rowCount,
  );
  const columnWidths = normalize(
    colWidthsRaw.length === columnCount ? colWidthsRaw : Array(columnCount).fill(width / columnCount),
    width,
    columnCount,
  );
  const combinedHeights = showHeader ? [headerHeight, ...rowHeights] : [...rowHeights];
  const rows = combinedHeights.length;
  const headerRowIndices = headerRowIndicesRaw
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n >= 0 && n < rows);
  const headerColIndices = headerColIndicesRaw
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n >= 0 && n < columnCount);
  const strokeColorRaw = (props.strokeColor ?? (attrs?.strokeColor as string | undefined)) as string | undefined;
  const gridColor = escapeSvgAttr(
    (strokeColorRaw && strokeColorRaw.trim()) ||
      (props.gridColor ?? (attrs?.gridColor as string | undefined)) ||
      '#97a3b6',
  );
  const fillColorRaw = (props.fillColor ?? (attrs?.fillColor as string | undefined)) as string | undefined;
  const cellFillColor = escapeSvgAttr(
    (fillColorRaw && fillColorRaw.trim()) ||
      (props.cellFillColor ?? (attrs?.cellFillColor as string | undefined)) ||
      '#FFFFFF',
  );
  const headerFillColor = escapeSvgAttr((props.headerFillColor ?? (attrs?.headerFillColor as string | undefined)) || '#F1F5F9');
  const textColor = escapeSvgAttr((props.textColor ?? (attrs?.textColor as string | undefined)) || '#0F172A');
  const baseFontSize = (props.fontSize ?? (attrs?.fontSize as number | undefined)) || 14;
  const baseFontFamily = (props.fontFamily ?? (attrs?.fontFamily as string | undefined)) || 'Inter, system-ui, -apple-system, sans-serif';
  const strokeWidth = Math.max(1, Number((props.strokeWidth ?? (attrs?.strokeWidth as number | undefined)) ?? 1));
  const dash = (props.dash ?? (attrs?.dash as number[] | undefined)) || [];
  const dashEnabled = ((props.dashEnabled ?? (attrs as any)?.dashEnabled) === true) && dash.length > 0;
  const cellTextMap = ((props.cellText ?? (attrs as any)?.cellText) as Record<string, string> | undefined) ?? {};
  const cellStyleMap = ((props.cellStyles ?? (attrs as any)?.cellStyles) as Record<string, any> | undefined) ?? {};
  const mergedRegions = (Array.isArray((props.mergedRegions ?? (attrs as any)?.mergedRegions)) ? (props.mergedRegions ?? (attrs as any)?.mergedRegions) : []) as Array<{
    startRow: number;
    startCol: number;
    endRow: number;
    endCol: number;
  }>;

  const rowStarts: number[] = [0];
  for (let i = 0; i < combinedHeights.length; i += 1) {
    rowStarts.push((rowStarts[i] ?? 0) + combinedHeights[i]);
  }
  const colStarts: number[] = [0];
  for (let i = 0; i < columnWidths.length; i += 1) {
    colStarts.push((colStarts[i] ?? 0) + columnWidths[i]);
  }

  const parts: string[] = [];
  parts.push(
    `<rect x="0" y="0" width="${formatSvgNumber(width)}" height="${formatSvgNumber(height)}" rx="6" ry="6" fill="${cellFillColor}" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
  );

  if (showHeader) {
    parts.push(
      `<rect x="0" y="0" width="${formatSvgNumber(width)}" height="${formatSvgNumber(headerHeight)}" rx="6" ry="6" fill="${headerFillColor}" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
    );
  }

  if (headerRowIndices.length > 0 || headerColIndices.length > 0) {
    for (let r = 0; r < rows; r += 1) {
      const isHeaderRow = headerRowIndices.includes(r);
      const y1 = rowStarts[r] ?? 0;
      const y2 = rowStarts[r + 1] ?? height;
      const h = Math.max(0, y2 - y1);
      for (let c = 0; c < columnWidths.length; c += 1) {
        if (!isHeaderRow && !headerColIndices.includes(c)) continue;
        const x1 = colStarts[c] ?? 0;
        const x2 = colStarts[c + 1] ?? width;
        const w = Math.max(0, x2 - x1);
        const isTop = r === 0;
        const isBottom = r === rowHeights.length - 1;
        const isLeft = c === 0;
        const isRight = c === columnWidths.length - 1;
        const rx = isTop && isLeft ? 6 : isTop && isRight ? 6 : isBottom && isRight ? 6 : isBottom && isLeft ? 6 : 0;
        parts.push(
          `<rect x="${formatSvgNumber(x1)}" y="${formatSvgNumber(y1)}" width="${formatSvgNumber(w)}" height="${formatSvgNumber(h)}" rx="${formatSvgNumber(rx)}" ry="${formatSvgNumber(rx)}" fill="${headerFillColor}" />`,
        );
      }
    }
  }

  const findMergedRegion = (row: number, col: number) => {
    for (const r of mergedRegions) {
      if (!r) continue;
      const startRow = Number(r.startRow);
      const startCol = Number(r.startCol);
      const endRow = Number(r.endRow);
      const endCol = Number(r.endCol);
      if (![startRow, startCol, endRow, endCol].every(Number.isFinite)) continue;
      if (row >= startRow && row <= endRow && col >= startCol && col <= endCol) {
        return { startRow, startCol, endRow, endCol };
      }
    }
    return null;
  };

  for (let r = 0; r < rows; r += 1) {
    const y1 = rowStarts[r] ?? 0;
    for (let c = 0; c < columnWidths.length; c += 1) {
      const mr = findMergedRegion(r, c);
      if (mr && !(mr.startRow === r && mr.startCol === c)) continue;
      const startRow = mr ? mr.startRow : r;
      const endRow = mr ? mr.endRow : r;
      const startCol = mr ? mr.startCol : c;
      const endCol = mr ? mr.endCol : c;
      const x1 = colStarts[startCol] ?? 0;
      const x2 = colStarts[endCol + 1] ?? width;
      const y2 = rowStarts[endRow + 1] ?? height;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const key = `${r},${c}`;
      const style = (cellStyleMap[key] ?? {}) as Record<string, unknown>;
      const fill = typeof style.fillColor === 'string' ? style.fillColor : null;
      if (!fill) continue;
      const isTop = startRow === 0;
      const isBottom = endRow === rows - 1;
      const isLeft = startCol === 0;
      const isRight = endCol === columnWidths.length - 1;
      const rx = isTop && isLeft ? 6 : isTop && isRight ? 6 : isBottom && isRight ? 6 : isBottom && isLeft ? 6 : 0;
      parts.push(
        `<rect x="${formatSvgNumber(x1)}" y="${formatSvgNumber(y1)}" width="${formatSvgNumber(w)}" height="${formatSvgNumber(h)}" rx="${formatSvgNumber(rx)}" ry="${formatSvgNumber(rx)}" fill="${escapeSvgAttr(fill)}" />`,
      );
    }
  }

  let xCursor = 0;
  for (let i = 1; i < columnCount; i += 1) {
    xCursor += columnWidths[i - 1];
    const x = xCursor;
    parts.push(
      `<line x1="${formatSvgNumber(x)}" y1="0" x2="${formatSvgNumber(x)}" y2="${formatSvgNumber(height)}" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
    );
  }

  for (let i = 1; i < rows; i += 1) {
    const y = rowStarts[i] ?? 0;
    parts.push(
      `<line x1="0" y1="${formatSvgNumber(y)}" x2="${formatSvgNumber(width)}" y2="${formatSvgNumber(y)}" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
    );
  }

  if (mergedRegions.length > 0) {
    for (let i = 0; i < mergedRegions.length; i += 1) {
      const mr = mergedRegions[i];
      const startRow = Number(mr?.startRow);
      const startCol = Number(mr?.startCol);
      const endRow = Number(mr?.endRow);
      const endCol = Number(mr?.endCol);
      if (![startRow, startCol, endRow, endCol].every(Number.isFinite)) continue;
      if (endRow < startRow || endCol < startCol) continue;
      const x1 = colStarts[startCol] ?? 0;
      const x2 = colStarts[endCol + 1] ?? width;
      const y1 = rowStarts[startRow] ?? 0;
      const y2 = rowStarts[endRow + 1] ?? height;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      if (w <= 0 || h <= 0) continue;
      const fill = (showHeader && startRow === 0) || (showHeaderColumn && startCol === 0) ? headerFillColor : cellFillColor;
      parts.push(
        `<rect x="${formatSvgNumber(x1)}" y="${formatSvgNumber(y1)}" width="${formatSvgNumber(w)}" height="${formatSvgNumber(h)}" fill="${fill}" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
      );
    }
  }

  parts.push(
    `<rect x="0" y="0" width="${formatSvgNumber(width)}" height="${formatSvgNumber(height)}" rx="6" ry="6" fill="none" stroke="${gridColor}" stroke-width="${formatSvgNumber(strokeWidth)}"${dashEnabled ? ` stroke-dasharray="${dash.map(formatSvgNumber).join(' ')}"` : ''} />`,
  );

  for (let r = 0; r < rows; r += 1) {
    const y1 = rowStarts[r] ?? 0;
    for (let c = 0; c < columnWidths.length; c += 1) {
      const mr = findMergedRegion(r, c);
      if (mr && !(mr.startRow === r && mr.startCol === c)) continue;
      const startRow = mr ? mr.startRow : r;
      const endRow = mr ? mr.endRow : r;
      const startCol = mr ? mr.startCol : c;
      const endCol = mr ? mr.endCol : c;
      const x1 = colStarts[startCol] ?? 0;
      const x2 = colStarts[endCol + 1] ?? width;
      const y2 = rowStarts[endRow + 1] ?? height;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      if (w <= 0 || h <= 0) continue;
      const key = `${r},${c}`;
      const style = (cellStyleMap[key] ?? {}) as Record<string, unknown>;
      const borderColor = typeof style.borderColor === 'string' ? style.borderColor : null;
      const borderWidth = typeof style.borderWidth === 'number' && style.borderWidth > 0 ? style.borderWidth : 0;
      const borderDash = Array.isArray(style.borderDash) ? style.borderDash.filter((n) => typeof n === 'number' && Number.isFinite(n)) : [];
      if (borderColor && borderWidth > 0) {
        parts.push(
          `<rect x="${formatSvgNumber(x1)}" y="${formatSvgNumber(y1)}" width="${formatSvgNumber(w)}" height="${formatSvgNumber(h)}" fill="none" stroke="${escapeSvgAttr(borderColor)}" stroke-width="${formatSvgNumber(borderWidth)}"${borderDash.length > 0 ? ` stroke-dasharray="${borderDash.map(formatSvgNumber).join(' ')}"` : ''} />`,
        );
      }
      const rawText = cellTextMap[key];
      if (!rawText || !rawText.trim()) continue;
      const cellTextColor = typeof style.textColor === 'string' ? style.textColor : textColor;
      const cellFontSize = typeof style.fontSize === 'number' ? style.fontSize : baseFontSize;
      const cellFontFamily = typeof style.fontFamily === 'string' ? style.fontFamily : baseFontFamily;
      const headerRow = headerRowIndices.includes(startRow);
      const headerCol = headerColIndices.includes(startCol);
      const fontWeight = (style.fontWeight as string | undefined) ?? (headerRow || headerCol ? 'bold' : 'normal');
      const fontStyle = (style.fontStyle as string | undefined) ?? 'normal';
      const textDecoration = typeof style.textDecoration === 'string' ? style.textDecoration : undefined;
      const textAlign = (style.textAlign as string | undefined) ?? 'center';
      const verticalAlign = (style.verticalAlign as string | undefined) ?? 'middle';
      const rotation =
        typeof style.textRotation === 'number' && Number.isFinite(style.textRotation) ? style.textRotation : 0;
      const textRect = getTableCellTextRect({ x: x1, y: y1, w, h }, style as any);
      let x = textRect.x + textRect.w / 2;
      let y = textRect.y + textRect.h / 2;
      let textAnchor: 'start' | 'middle' | 'end' = 'middle';
      let dominantBaseline = 'middle';
      if (rotation === 0) {
        if (textAlign === 'left') {
          x = textRect.x;
          textAnchor = 'start';
        } else if (textAlign === 'right') {
          x = textRect.x + textRect.w;
          textAnchor = 'end';
        }
        if (verticalAlign === 'top') {
          y = textRect.y;
          dominantBaseline = 'hanging';
        } else if (verticalAlign === 'bottom') {
          y = textRect.y + textRect.h;
          dominantBaseline = 'text-after-edge';
        } else {
          y = textRect.y + textRect.h / 2;
          dominantBaseline = 'middle';
        }
        parts.push(
          renderMultilineText(rawText, {
            x,
            y,
            color: cellTextColor,
            fontSize: cellFontSize,
            fontFamily: cellFontFamily,
            fontWeight,
            fontStyle,
            textDecoration,
            textAnchor,
            dominantBaseline,
            lineHeightPx: cellFontSize * 1.2,
          }),
        );
      } else {
        const cx = textRect.x + textRect.w / 2;
        const cy = textRect.y + textRect.h / 2;
        const textSvg = renderMultilineText(rawText, {
          x: cx,
          y: cy,
          color: cellTextColor,
          fontSize: cellFontSize,
          fontFamily: cellFontFamily,
          fontWeight,
          fontStyle,
          textDecoration,
          textAnchor: 'middle',
          dominantBaseline: 'middle',
          lineHeightPx: cellFontSize * 1.2,
        });
        const cleaned = textSvg.replace('<text ', `<text transform="rotate(${formatSvgNumber(rotation)}, ${formatSvgNumber(cx)}, ${formatSvgNumber(cy)})" `);
        parts.push(cleaned);
      }
    }
  }

  const hasAnyCellText = Object.values(cellTextMap).some((value) => typeof value === 'string' && value.trim().length > 0);
  if (props.label && !hasAnyCellText) {
    parts.push(
      renderCenteredLabel(props.label, width, height, {
        color: textColor,
        fontSize: 14,
        fontFamily: 'Inter, sans-serif',
        padding: 12,
      }),
    );
  }

  return parts.join('');
};

export const TableShapeManifest: ShapeManifest<TableShapeProps> = {
  id: 'table',
  version: '1.0.0',
  category: 'table',
  propsSchema: tableShapeProps,
  defaultProps: () => ({
    rowCount: 3,
    columnCount: 3,
    headerHeight: DEFAULT_HEADER,
    showHeader: true,
    headerFillColor: '#F1F5F9',
    cellFillColor: '#FFFFFF',
    gridColor: '#97a3b6',
    textColor: '#0F172A',
  }),
  defaultDimensions: DEFAULT_DIMS,
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: false,
    connectable: true,
  },
  renderers: {
    konvaNative: renderKonva,
    svg: ({ viewBox, props }) => renderSvg({ width: viewBox.w, height: viewBox.h }, props),
  },
};
